// HelpBox — B4: the ONE optional "how this would help" box that replaces both required
// narratives. Shown only when need is in the gap, and never on a service-animal item (those
// carry the two permitted questions instead). Rendered by BOTH the public requester form and the
// console's staff fill-in, so they cannot drift.
//
// There is no field for a diagnosis here: the helper under the label says to leave out the
// disability, symptoms and treatment, the box holds 300 characters, and the counter appears from
// 250. Every input has a <label htmlFor> (B10); the helper is the description.
//
//   cd apps/web && npx tsx --test src/components/rra/HelpBox.test.ts

import {
  HELP_HEADING,
  RRA_HELP_MAX,
  helpCounter,
  helpHelper,
  helpLabel,
  type RraCopySubject,
} from '@/lib/rraCopy';

export function HelpBox(props: {
  /** Prefix for the ids (unique per item on the page). */
  idPrefix: string;
  value: string;
  onChange: (value: string) => void;
  /** Whose voice the label and helper are in (B2). */
  subject: RraCopySubject;
  /** An animal item: the label names the animal. */
  animal: boolean;
  animalName?: string | undefined;
  disabled?: boolean;
  /** The H2 (`HELP_HEADING`) — pass null where the surrounding panel already has a heading (staff fill-in). */
  heading?: string | null;
  /** Override the label (the staff fill-in says "Requester says — how it would help"). */
  label?: string;
  /** A server refusal for this box (e.g. over 300). */
  error?: string | undefined;
}): React.JSX.Element {
  const { idPrefix, value, subject, disabled = false } = props;
  const inputId = `${idPrefix}-help`;
  const describedBy = `${inputId}-helper`;
  const counter = helpCounter(value.length);
  const heading = props.heading === undefined ? HELP_HEADING : props.heading;
  const label =
    props.label ?? helpLabel(subject, { animal: props.animal, animalName: props.animalName });
  return (
    <div className="stack" style={{ gap: 'var(--space-2)' }}>
      {heading ? <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>{heading}</h2> : null}
      <div className="field">
        <label htmlFor={inputId}>{label}</label>
        <span className="helper" id={describedBy}>
          {helpHelper(subject)}
        </span>
        <textarea
          id={inputId}
          className="input"
          rows={3}
          maxLength={RRA_HELP_MAX}
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          {...(props.error ? { 'aria-invalid': 'true' as const } : {})}
          onChange={(e) => props.onChange(e.target.value)}
        />
        {props.error ? (
          <span className="helper" data-state="error" role="alert">
            {props.error}
          </span>
        ) : null}
        {counter ? (
          <span className="helper" style={{ textAlign: 'right' }} aria-live="polite">
            {counter}
          </span>
        ) : null}
      </div>
    </div>
  );
}
